import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAddendums,
  getAddendumById,
  getAddendumsByPKS,
  createAddendum,
  updateAddendum,
  deleteAddendum,
  submitAddendum,
  approveAddendum,
  rejectAddendum,
  activateAddendum,
} from "libs/api/addendum";

const wrapThunk = (type, apiFn) =>
  createAsyncThunk(
    type,
    async ({ cbPending, cbFulfilled, cbRejected, cbFinally, ...args }) => {
      try {
        typeof cbPending === "function" && cbPending();
        const response = await apiFn(args);
        typeof cbFulfilled === "function" && cbFulfilled(response);
        return response?.data;
      } catch (error) {
        if (error.response) {
          typeof cbRejected === "function" && cbRejected(error.response);
          throw JSON.stringify(error.response.data);
        } else {
          typeof cbRejected === "function" && cbRejected(error);
          throw JSON.stringify({ message: error.message });
        }
      } finally {
        typeof cbFinally === "function" && cbFinally();
      }
    },
  );

// ── GET LIST ──────────────────────────────────────────────────────────────────
export const getAddendumsThunk = createAsyncThunk(
  "addendum/getAll",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, queryParams = "" }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await getAddendums(queryParams);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// ── GET BY ID ─────────────────────────────────────────────────────────────────
export const getAddendumByIdThunk = createAsyncThunk(
  "addendum/getById",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, id }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await getAddendumById(id);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// ── GET BY PKS ────────────────────────────────────────────────────────────────
export const getAddendumsByPKSThunk = createAsyncThunk(
  "addendum/getByPKS",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, pks_id }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await getAddendumsByPKS(pks_id);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// ── GET BY CONTRACT ───────────────────────────────────────────────────────────
export const getAddendumsByContractThunk = createAsyncThunk(
  "addendum/getByContract",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, contract_id }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await getAddendums(`?contract_id=${contract_id}`);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// ── CREATE ────────────────────────────────────────────────────────────────────
export const createAddendumThunk = createAsyncThunk(
  "addendum/create",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, body, accessToken }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await createAddendum(body, accessToken);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// ── UPDATE ────────────────────────────────────────────────────────────────────
export const updateAddendumThunk = createAsyncThunk(
  "addendum/update",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, id, body, accessToken }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await updateAddendum(id, body, accessToken);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// ── DELETE ────────────────────────────────────────────────────────────────────
export const deleteAddendumThunk = createAsyncThunk(
  "addendum/delete",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, id }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await deleteAddendum(id);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

// ── WORKFLOW ──────────────────────────────────────────────────────────────────
export const submitAddendumThunk = createAsyncThunk(
  "addendum/submit",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, id }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await submitAddendum(id);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const approveAddendumThunk = createAsyncThunk(
  "addendum/approve",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, id }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await approveAddendum(id);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const rejectAddendumThunk = createAsyncThunk(
  "addendum/reject",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, id, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await rejectAddendum(id, body);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);

export const activateAddendumThunk = createAsyncThunk(
  "addendum/activate",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, id }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await activateAddendum(id);
      typeof cbFulfilled === "function" && cbFulfilled(response);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  },
);
